import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHelpdeskRuntime } from '../model/runtime';
import { Button, Text } from '@shared/ui';
import type { PresentationThread as HelpdeskThread } from '../model/runtime';

type Attachment = HelpdeskThread['task']['files'][number];
const imageTypes = new Set(['image/png', 'image/jpeg', 'image/webp']);

export function HelpdeskAttachment({ taskUuid, file }: { taskUuid: string; file: Attachment }) {
  const { t } = useTranslation();
  const runtime = useHelpdeskRuntime();
  const [preview, setPreview] = useState<{ scope: string; url: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const urls = useRef<string[]>([]);
  const previewUrl = useRef<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const active = useRef(true);
  const isImage = imageTypes.has(file.mimeType ?? '') || /\.(png|jpe?g|webp)$/i.test(file.name);
  const isPdf = file.mimeType === 'application/pdf' || /\.pdf$/i.test(file.name);
  const available = file.availability === 'available' && Boolean(file.fileRef);
  const path = `/api/v1/tasks/${encodeURIComponent(taskUuid)}/files/${encodeURIComponent(file.fileRef ?? '')}/download`;
  const previewScope = JSON.stringify([
    taskUuid,
    file.fileRef,
    file.name,
    file.mimeType,
    file.availability,
  ]);
  const currentPreview =
    preview?.scope === previewScope && available && isImage ? preview.url : null;

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      controller.current?.abort();
      urls.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      urls.current = [];
      previewUrl.current = null;
    };
  }, []);

  useEffect(() => {
    controller.current?.abort();
    const oldPreviewUrl = previewUrl.current;
    if (oldPreviewUrl !== null) {
      URL.revokeObjectURL(oldPreviewUrl);
      urls.current = urls.current.filter((url) => url !== oldPreviewUrl);
      previewUrl.current = null;
    }
    setPreview(null);
    setLoading(false);
    setFailed(false);
    if (!available || !isImage) return;

    const request = new AbortController();
    controller.current = request;
    setLoading(true);
    void runtime
      .getFile(taskUuid, file.fileRef ?? '', request.signal)
      .then((blob) => {
        if (request.signal.aborted || !active.current) return null;
        // Only trusted raster MIME types are rendered; never execute SVG or HTML attachments.
        if (!imageTypes.has(blob.type)) {
          setLoading(false);
          setFailed(true);
          return null;
        }
        const url = URL.createObjectURL(blob);
        urls.current.push(url);
        previewUrl.current = url;
        setPreview({ scope: previewScope, url });
        setLoading(false);
        return null;
      })
      .catch(() => {
        if (request.signal.aborted || !active.current) return null;
        setLoading(false);
        setFailed(true);
      });
    return () => {
      request.abort();
    };
  }, [
    runtime,
    taskUuid,
    file.fileRef,
    available,
    file.availability,
    file.mimeType,
    file.name,
    isImage,
    path,
    previewScope,
    attempt,
  ]);

  const openFile = () => {
    const tab = window.open('', '_blank');
    if (!tab) {
      setFailed(true);
      return;
    }
    tab.opener = null;
    const request = new AbortController();
    controller.current = request;
    setLoading(true);
    setFailed(false);
    void runtime
      .getFile(taskUuid, file.fileRef ?? '', request.signal)
      .then((blob) => {
        if (request.signal.aborted || !active.current) {
          tab.close();
          return null;
        }
        const url = URL.createObjectURL(blob);
        urls.current.push(url);
        if (imageTypes.has(blob.type) || blob.type === 'application/pdf') {
          tab.location.replace(url);
        } else {
          tab.close();
          const link = document.createElement('a');
          link.href = url;
          link.download = file.name;
          link.click();
        }
        setLoading(false);
        return null;
      })
      .catch(() => {
        tab.close();
        if (request.signal.aborted || !active.current) return null;
        setLoading(false);
        setFailed(true);
      });
  };

  if (!available)
    return (
      <Text color="muted">
        {t('helpdesk.thread.unavailableFile', '{{name}} — unavailable', { name: file.name })}
      </Text>
    );
  return (
    <div className="space-y-2">
      {currentPreview ? (
        <a
          href={currentPreview}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={file.name}
          className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <img
            src={currentPreview}
            alt={file.name}
            onError={() => {
              URL.revokeObjectURL(currentPreview);
              urls.current = urls.current.filter((url) => url !== currentPreview);
              previewUrl.current = null;
              setPreview(null);
              setFailed(true);
            }}
            className="max-h-96 w-auto max-w-full rounded-lg object-contain"
          />
        </a>
      ) : (
        <Button
          variant="link"
          className="h-auto whitespace-normal p-0 text-left"
          disabled={loading}
          onClick={
            failed && isImage
              ? () => {
                  setAttempt((value) => value + 1);
                }
              : openFile
          }
        >
          {loading
            ? t('common.loading', 'Loading...')
            : failed
              ? t('common.retry', 'Retry')
              : !isImage && !isPdf
                ? t('helpdesk.thread.downloadFile', 'Download {{name}}', { name: file.name })
                : file.name}
        </Button>
      )}
      {failed ? (
        <Text size="sm" color="muted">
          {t('common.somethingWentWrong', 'Something went wrong')}
        </Text>
      ) : null}
    </div>
  );
}
